import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Save, Search, LayoutGrid } from 'lucide-react';
import { ZONE_ANALYSIS_DATA, ZONE_LABELS } from './ZoneAnalysisData';
import { pickLang, LANGUAGE_OPTIONS } from './i18n/languageOptions';
import useIsMobile from '../../../hooks/useIsMobile';
import { useLeaveConfirm } from './Configure/components/ConfirmLeaveDialog';
import ReplaceableImage from './Configure/components/ReplaceableImage';

// NOTE: was a flat, unscoped key shared across every one of a user's projects
// — scoped per-project below (component-level const shadows this).
// v3: buildInitialHtml renders in the selected language via pickLang, so every
// v2 (English-only) richHtml regenerates once on load.
const HTML_VERSION = 3;

/* ── Flatten all items from all zones ─────────────────────────────── */
const ALL_ITEMS = ZONE_ANALYSIS_DATA.flatMap(zone =>
  zone.items.map(item => ({ ...item, zoneName: zone.zoneName, zoneId: zone.zoneId }))
);
const TOTAL = ALL_ITEMS.length;

/* ── Build initial HTML from item fields, in the given language ──── */
export const buildInitialHtml = (item, lang = 'en') => {
  const L = (k) => pickLang(ZONE_LABELS[k], lang);
  const parts = [];
  const purpose = pickLang(item.purpose, lang);
  const description = pickLang(item.description, lang);
  const specialNote = pickLang(item.specialNote, lang);
  if (purpose)     parts.push(`<h2>${L('purpose')}</h2><p>${purpose}</p>`);
  if (description) parts.push(`<h2>${L('description')}</h2><p>${description}</p>`);
  if (specialNote) parts.push(`<h2>${L('specialNote')}</h2><p>${specialNote}</p>`);
  return parts.join('') || '<p><br></p>';
};

// True when `html` is one of the 8 canonical default-language renders for this
// item (nothing the user has typed over yet) — safe to re-translate.
export const isStillDefaultHtml = (item, html) => {
  if (!html) return true;
  return LANGUAGE_OPTIONS.some(({ code }) => buildInitialHtml(item, code) === html);
};

const migrateItem = (item, lang = 'en') => {
  if (item.richHtml !== undefined && item._htmlVersion === HTML_VERSION) return item;
  return { ...item, richHtml: buildInitialHtml(item, lang), _htmlVersion: HTML_VERSION };
};

/* ── Toggle ──────────────────────────────────────────────────────── */
const Toggle = ({ checked, onChange }) => (
  <label style={{ position:'relative', display:'inline-block', width:44, height:24, cursor:'pointer', flexShrink:0 }}>
    <input type="checkbox" checked={checked} onChange={onChange} style={{ opacity:0, width:0, height:0 }} />
    <span style={{ position:'absolute', inset:0, background:checked?'#00A19D':'#CBD5E1', borderRadius:24, transition:'background .2s' }} />
    <span style={{ position:'absolute', top:2, left:checked?22:2, width:20, height:20, background:'#fff', borderRadius:'50%', transition:'left .2s', boxShadow:'0 1px 4px rgba(0,0,0,.18)' }} />
  </label>
);

/* ── Rich Text Editor ────────────────────────────────────────────── */
const RichEditor = React.memo(function RichEditor({ value, onChange, itemId }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || '<p><br></p>';
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const exec = useCallback((cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const formatBlock = useCallback((tag) => {
    editorRef.current?.focus();
    document.execCommand('formatBlock', false, tag);
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const handleInput = useCallback(() => {
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const btn = (extra = {}) => ({
    border: '1px solid #e8eaed', borderRadius: 4,
    background: '#fff', color: '#3c4043', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: 26, padding: '0 7px', fontSize: '0.74rem', fontFamily: 'inherit',
    ...extra,
  });

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      <div style={{ display:'flex', alignItems:'center', gap:3, padding:'5px 8px', background:'#fafafa', borderBottom:'1px solid #e8eaed', flexWrap:'wrap' }}>
        <select
          onMouseDown={e => e.preventDefault()}
          onChange={e => { formatBlock(e.target.value); e.target.value = ''; }}
          defaultValue=""
          style={{ fontSize:'0.71rem', fontWeight:600, border:'1px solid #e8eaed', borderRadius:4, padding:'2px 6px', color:'#3c4043', background:'#fff', cursor:'pointer', outline:'none', fontFamily:'inherit', height:26 }}
        >
          <option value="" disabled>Format</option>
          <option value="p">Paragraph</option>
          <option value="h2">H2 — Sub Heading</option>
          <option value="h3">H3 — Title</option>
        </select>
        <span style={{ width:1, height:16, background:'#e8eaed', margin:'0 2px' }} />
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('bold');}}      style={btn({fontWeight:800,width:26,padding:0})}>B</button>
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('italic');}}    style={btn({fontStyle:'italic',width:26,padding:0})}>I</button>
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('underline');}} style={btn({textDecoration:'underline',width:26,padding:0})}>U</button>
        <span style={{ width:1, height:16, background:'#e8eaed', margin:'0 2px' }} />
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('insertUnorderedList');}} style={btn()}>• List</button>
        <span style={{ flex:1 }} />
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('removeFormat');}} style={btn({color:'#80868b',fontSize:'0.67rem'})}>Clear</button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        data-placeholder="Enter description…"
        className="zan-editor"
        style={{ minHeight:90, padding:'10px 14px', outline:'none', fontFamily:"'Inter','Segoe UI',system-ui,sans-serif", fontSize:'0.84rem', color:'#202124', lineHeight:1.65 }}
      />
    </div>
  );
});

/* ── ZoneItemCard ────────────────────────────────────────────────── */
const ZoneItemCard = React.memo(function ZoneItemCard({ item, onToggle, onHtmlChange, onImageChange, language = 'en' }) {
  // Hand-edited content is intentionally never auto-retranslated on a
  // language switch (see the [language] effect below) — this badge is the
  // only signal the user gets that this item now needs manual re-editing
  // per language instead of following the report language automatically.
  const isEdited = !isStillDefaultHtml(item, item.richHtml);
  return (
    <div style={{
      background:'#fff', borderRadius:12,
      border:`1px solid ${item.enabled ? '#00A19D' : '#e2e8f0'}`,
      boxShadow: item.enabled ? '0 1px 12px rgba(0,161,157,.08)' : '0 1px 3px rgba(0,0,0,.04)',
      overflow:'hidden', transition:'border-color .2s',
    }}>
      {/* ── Header ── */}
      <div style={{
        display:'flex', alignItems:'center', gap:10, padding:'10px 14px',
        background: item.enabled ? '#f0fdfc' : '#fafafa',
        borderBottom:'1px solid #f1f5f9',
      }}>
        <div style={{ width:28, height:28, borderRadius:6, flexShrink:0, background:item.enabled?'#00A19D':'#e2e8f0', color:item.enabled?'#fff':'#94a3b8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.72rem', fontWeight:700 }}>
          {item.id}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:'0.9rem', fontWeight:700, color:item.enabled?'#0f172a':'#94a3b8', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {item.name}
          </div>
          <div style={{ fontSize:'0.7rem', color:'#64748b', marginTop:1, fontWeight:500 }}>
            {item.zoneName}
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:7, flexShrink:0 }}>
          {isEdited && (
            <span
              title="Edited — won't auto-translate when you switch language. Edit it again in each language you need."
              style={{ fontSize:'0.62rem', color:'#b45309', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:4, padding:'1px 6px', fontWeight:700 }}
            >
              Edited
            </span>
          )}
          <span style={{ fontSize:'0.68rem', color:item.enabled?'#00857E':'#94a3b8', fontWeight:600 }}>
            {item.enabled ? 'ON' : 'OFF'}
          </span>
          <Toggle checked={item.enabled} onChange={onToggle} />
        </div>
      </div>

      {/* ── Image (fixed path) ── */}
      <div style={{ background:'#f8fafc', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'center', padding:'10px 14px' }}>
        <ReplaceableImage
          src={item.customImage || `/zone-analysis/zone${item.id}.png`}
          isCustom={!!item.customImage}
          alt={item.name}
          onReplace={url => onImageChange(url)}
          onReset={() => onImageChange(null)}
        />
      </div>

      {/* ── Editor ── */}
      <div style={{ padding:'12px 14px 14px' }}>
        <RichEditor itemId={`${item.id}-${language}`} value={item.richHtml} onChange={onHtmlChange} />
      </div>
    </div>
  );
});

/* ── Main Component ───────────────────────────────────────────────── */
export default function ZoneAnalysis({ openModal = false, onModalClose = null, onReportGenerated = null, projectId = null, language = 'en' }) {
  // Mobile: this modal's own header/toolbar/grid were desktop-only —
  // the badges + 3 header buttons overflowed a phone header, the zone
  // dropdown/search/toolbar buttons never fit on one line, and the
  // grid's minmax(520px,...) columns forced horizontal scroll. Fixed
  // below via isMobile-gated styles only; desktop keeps every original
  // style untouched.
  const isMobile = useIsMobile();
  const STORAGE_KEY = `zone_analysis_data_${projectId || 'new'}`;
  const [itemData, setItemData] = useState([]);
  const [search, setSearch]     = useState('');
  const [zoneFilter, setZoneFilter] = useState('');  // '' = all zones
  const [isSaving, setIsSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const scrollRef = useRef(null);

  /* ── Load from localStorage ── */
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setItemData(ALL_ITEMS.map(d => {
          const ov = parsed.find(p => p.id === d.id);
          return migrateItem(ov ? { ...d, ...ov } : { ...d, enabled: false }, language);
        }));
      } else {
        setItemData(ALL_ITEMS.map(d => migrateItem({ ...d, enabled: false }, language)));
      }
    } catch {
      setItemData(ALL_ITEMS.map(d => migrateItem({ ...d, enabled: false }, language)));
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Language switch: re-render any item's richHtml that is STILL a default
  // (never hand-edited) into the newly selected language. User edits untouched.
  useEffect(() => {
    setItemData(prev => {
      if (prev.length === 0) return prev;
      let changed = false;
      const next = prev.map(d => {
        if (!isStillDefaultHtml(d, d.richHtml)) return d;
        const translated = buildInitialHtml(d, language);
        if (translated === d.richHtml) return d;
        changed = true;
        return { ...d, richHtml: translated };
      });
      return changed ? next : prev;
    });
  }, [language]);

  const handleToggle    = id  => setItemData(p => p.map(d => d.id === id ? { ...d, enabled: !d.enabled } : d));
  const handleToggleAll = val => setItemData(p => p.map(d => ({ ...d, enabled: val })));
  const handleToggleZone = (zoneName, val) => setItemData(p => p.map(d => d.zoneName === zoneName ? { ...d, enabled: val } : d));
  const handleHtml = useCallback((id, html) => setItemData(p => p.map(d => d.id === id ? { ...d, richHtml: html } : d)), []);
  // Replace / reset a picture (customImage = the user's own picture, undefined = original)
  const handleImage = useCallback((id, url) => setItemData(p => p.map(d => d.id === id ? { ...d, customImage: url || undefined } : d)), []);

  const handleSave = () => {
    setIsSaving(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(itemData));
      if (onReportGenerated) onReportGenerated({ reportData: itemData });
      setSavedMsg('All changes saved');
      setTimeout(() => setSavedMsg(''), 3000);
    } catch { setSavedMsg('Save failed'); }
    setIsSaving(false);
  };

  const handleClose        = () => { if (onModalClose) onModalClose(); };
  const handleSaveAndClose = () => { handleSave(); handleClose(); };

  /* ── Filtered items ── */
  const filtered = useMemo(() => {
    return itemData.filter(d => {
      const matchZone   = !zoneFilter || d.zoneName === zoneFilter;
      const matchSearch = !search || d.name.toLowerCase().includes(search.toLowerCase()) || d.zoneName.toLowerCase().includes(search.toLowerCase());
      return matchZone && matchSearch;
    });
  }, [itemData, search, zoneFilter]);

  const enabledCount = itemData.filter(d => d.enabled).length;
  const zoneNames    = ZONE_ANALYSIS_DATA.map(z => z.zoneName);

  const leave = useLeaveConfirm({ onSave: handleSaveAndClose, onLeave: handleClose });

  if (!openModal) return null;

  return (
    /* z-index bumped above the mobile bottom toolbar (10100)/sheets — this
       used to render behind them on phone, looking like it never opened. */
    <div style={{ position:'fixed', inset:0, zIndex:10500, display:'flex', flexDirection:'column', background:'#f1f5f9', fontFamily:"'Inter','Segoe UI',system-ui,sans-serif", textAlign:'left' }}>

      <style>{`
        .zan-editor h2 { font-size:0.82rem; font-weight:700; color:#1e293b; margin:8px 0 1px; }
        .zan-editor h3 { font-size:0.8rem;  font-weight:600; color:#334155; margin:6px 0 1px; }
        .zan-editor p  { font-size:0.82rem; color:#475569; margin:1px 0 5px; line-height:1.65; }
        .zan-editor ul { font-size:0.82rem; color:#475569; padding-left:1.8em; margin:2px 0 6px; line-height:1.65; }
        .zan-editor li { margin-bottom:2px; }
        .zan-editor strong { font-weight:700; color:#1e293b; }
        .zan-editor em    { font-style:italic; }
        .zan-editor[data-placeholder]:empty::before { content:attr(data-placeholder); color:#cbd5e1; pointer-events:none; }
        .zan-editor:focus { outline:none; }
      `}</style>

      {/* ── Header ── */}
      <div style={{ background:'#0f172a', color:'#fff', padding: isMobile ? '0 14px' : '0 24px', display:'flex', alignItems:'center', justifyContent:'space-between', height:56, flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap: isMobile ? 10 : 14, minWidth:0 }}>
          <div style={{ width:32, height:32, borderRadius:8, background:'linear-gradient(135deg,#00857E,#00C4BF)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><LayoutGrid size={16} color="#fff" /></div>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:'0.94rem', fontWeight:700, lineHeight:1.2 }}>Zone Analysis</div>
            {!isMobile && <div style={{ fontSize:'0.68rem', color:'#64748b' }}>17 Vastu Zones &amp; Recommended Symbols</div>}
          </div>
          {!isMobile && (
            <>
              <div style={{ width:1, height:26, background:'rgba(255,255,255,.1)', margin:'0 4px' }} />
              <span style={{ fontSize:'0.72rem', background:'rgba(0,161,157,.2)', color:'#4ffffa', border:'1px solid rgba(0,161,157,.35)', borderRadius:20, padding:'2px 10px', fontWeight:600 }}>{enabledCount} enabled</span>
              <span style={{ fontSize:'0.72rem', background:'rgba(255,255,255,.06)', color:'#94a3b8', border:'1px solid rgba(255,255,255,.1)', borderRadius:20, padding:'2px 10px', fontWeight:600 }}>{TOTAL - enabledCount} disabled</span>
            </>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap: isMobile ? 6 : 8, flexShrink:0 }}>
          {savedMsg && !isMobile && <span style={{ fontSize:'0.76rem', color:'#4ade80', fontWeight:500 }}>&#10003; {savedMsg}</span>}
          {isMobile ? (
            <button onClick={handleSave} disabled={isSaving} title={isSaving ? 'Saving…' : 'Save Report'}
              style={{ background:'#00A19D', border:'none', color:'#fff', borderRadius:7, width:34, height:34, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', opacity:isSaving?.7:1, flexShrink:0 }}>
              <Save size={14} />
            </button>
          ) : (
            <>
              <button onClick={handleSave} disabled={isSaving}
                style={{ background:'#00A19D', border:'none', color:'#fff', borderRadius:7, padding:'7px 18px', fontWeight:600, fontSize:'0.8rem', cursor:'pointer', display:'flex', alignItems:'center', gap:5, opacity:isSaving?.7:1 }}>
                <Save size={13} />{isSaving ? 'Saving…' : 'Save Report'}
              </button>
              <button onClick={handleSaveAndClose}
                style={{ background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.12)', color:'#fff', borderRadius:7, padding:'7px 18px', fontWeight:600, fontSize:'0.8rem', cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}>
                <Save size={13} />Save &amp; Close
              </button>
            </>
          )}
          <button onClick={leave.requestClose}
            style={{ background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.1)', color:'#94a3b8', borderRadius:7, padding: isMobile ? '9px' : '7px 9px', cursor:'pointer', display:'flex', alignItems:'center', flexShrink:0 }}>
            <X size={15} />
          </button>
          {leave.dialog}
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div style={{ background:'#fff', borderBottom:'1px solid #e2e8f0', padding: isMobile ? '8px 14px' : '8px 24px', display:'flex', alignItems:'center', gap:10, flexShrink:0, flexWrap:'wrap' }}>
        {/* Zone dropdown */}
        <select
          value={zoneFilter}
          onChange={e => setZoneFilter(e.target.value)}
          style={{ height:34, border:'1.5px solid #e2e8f0', borderRadius:7, fontSize:'0.78rem', color:'#334155', outline:'none', fontFamily:'inherit', background:'#f8fafc', padding:'0 10px', cursor:'pointer', minWidth: isMobile ? 0 : 200, flex: isMobile ? '1 1 100%' : 'initial' }}
        >
          <option value="">All Zones ({ZONE_ANALYSIS_DATA.length})</option>
          {zoneNames.map(z => (
            <option key={z} value={z}>{z}</option>
          ))}
        </select>

        {/* Zone enable/disable all buttons */}
        {zoneFilter && (
          <>
            <button onClick={() => handleToggleZone(zoneFilter, true)}  style={tbBtn('#00A19D','#fff')}>Enable Zone</button>
            <button onClick={() => handleToggleZone(zoneFilter, false)} style={tbBtn('#fff','#64748b','#e2e8f0')}>Disable Zone</button>
          </>
        )}

        {!isMobile && <div style={{ width:1, height:22, background:'#e2e8f0' }} />}

        {/* Search */}
        <div style={{ position:'relative', flex: isMobile ? '1 1 100%' : '0 0 200px' }}>
          <Search size={13} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:'#94a3b8' }} />
          <input type="text" placeholder="Search item name…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ width:'100%', boxSizing:'border-box', paddingLeft:28, paddingRight:8, height:34, border:'1.5px solid #e2e8f0', borderRadius:7, fontSize:'0.78rem', color:'#334155', outline:'none', fontFamily:'inherit', background:'#f8fafc' }} />
        </div>

        {!isMobile && <div style={{ width:1, height:22, background:'#e2e8f0' }} />}
        {/* Highlight whichever matches the actual current state — same
            pattern as VastuDevtaGuide/NavgrahShanti/Feng Shui. "Enable All"
            was hardcoded teal/filled regardless of actual state, so it
            always looked active even when everything was really disabled. */}
        {(() => {
          const allEnabled  = itemData.length > 0 && itemData.every(d => d.enabled);
          const allDisabled = itemData.length > 0 && itemData.every(d => !d.enabled);
          return (
            <>
              <button onClick={() => handleToggleAll(true)}
                style={allEnabled ? tbBtn('#00A19D','#fff') : tbBtn('#fff','#64748b','#e2e8f0')}>Enable All</button>
              <button onClick={() => handleToggleAll(false)}
                style={allDisabled ? tbBtn('#00A19D','#fff') : tbBtn('#fff','#64748b','#e2e8f0')}>Disable All</button>
            </>
          );
        })()}
        <div style={{ marginLeft: isMobile ? 0 : 'auto', fontSize:'0.76rem', color:'#94a3b8', fontWeight:500 }}>
          {filtered.length} of {TOTAL} items
        </div>
      </div>

      {/* ── Cards Grid ── */}
      <div ref={scrollRef} style={{ flex:1, overflowY:'auto', padding: isMobile ? '14px 14px 30px' : '20px 24px 40px' }}>
        <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill,minmax(520px,1fr))', gap: isMobile ? 12 : 18 }}>
          {filtered.map(item => (
            <ZoneItemCard
              key={item.id}
              item={item}
              language={language}
              onToggle={() => handleToggle(item.id)}
              onHtmlChange={html => handleHtml(item.id, html)}
              onImageChange={url => handleImage(item.id, url)}
            />
          ))}
        </div>
        {filtered.length === 0 && (
          <div style={{ padding:'60px 20px', color:'#94a3b8', textAlign:'center' }}>
            <div style={{ fontSize:'2rem', marginBottom:10 }}>🔍</div>
            <div style={{ fontSize:'0.88rem', fontWeight:500 }}>
              {search ? `No match for "${search}"` : `No items in "${zoneFilter}"`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function tbBtn(bg, color, border) {
  return { background:bg, color, border:`1.5px solid ${border||bg}`, borderRadius:7, padding:'5px 13px', fontSize:'0.76rem', fontWeight:600, cursor:'pointer' };
}
